import { Button, Label, Select } from '@osago/ui';
import DatePicker from '@osago/ui/DatePicker';

import { PRODUCTS } from '../helpers/products';

export interface FilterState {
  dateFrom: string;
  dateTo: string;
  company: string;
  product: string;
  status: string;
}

interface AgreementsFilterProps {
  filters: FilterState;
  availableCompanies: string[];
  getCompanyTitle: (code: string) => string;
  onChange: (filters: FilterState) => void;
  onReset: () => void;
}

const MIN_DATE = new Date(2000, 0, 1);
const MAX_DATE = new Date(new Date().getFullYear() + 1, 11, 31);

// DatePicker работает с ДД.ММ.ГГГГ, а состояние фильтра — ISO (ГГГГ-ММ-ДД)
const isoToRu = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

const ruToIso = (ru: string) => {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(ru);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Активные' },
  { value: 'inactive', label: 'Неактивные' },
  { value: '', label: 'Все' },
];

const AgreementsFilter = ({ filters, availableCompanies, getCompanyTitle, onChange, onReset }: AgreementsFilterProps) => {
  const hasActiveFilters =
    filters.dateFrom ||
    filters.dateTo ||
    filters.company ||
    filters.product ||
    filters.status !== 'active';

  const companyOptions = [
    { value: '', label: 'Все' },
    ...Array.from(new Set(availableCompanies.map((c) => getCompanyTitle(c)))).map((label) => ({
      value: label,
      label,
    })),
  ];

  const productOptions = [{ value: '', label: 'Все' }, ...PRODUCTS];

  return (
    <div className="policy-filter">
      <div className="policy-filter__row">
        <div className="policy-filter__group">
          <Label variant="caption" className="policy-filter__label">Дата начала</Label>
          <DatePicker
            size="sm"
            value={isoToRu(filters.dateFrom)}
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(v) => onChange({ ...filters, dateFrom: ruToIso(v) })}
          />
        </div>
        <div className="policy-filter__group">
          <Label variant="caption" className="policy-filter__label">Дата окончания</Label>
          <DatePicker
            size="sm"
            value={isoToRu(filters.dateTo)}
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            onChange={(v) => onChange({ ...filters, dateTo: ruToIso(v) })}
          />
        </div>

        {availableCompanies.length > 0 && (
          <div className="policy-filter__group">
            <Label variant="caption" className="policy-filter__label">Страховая компания</Label>
            <Select
              size="sm"
              value={companyOptions.find((o) => o.value === filters.company)?.label ?? 'Все'}
              options={companyOptions}
              getOptionKey={(o) => o.value}
              getOptionLabel={(o) => o.label}
              onSelect={(o) => onChange({ ...filters, company: o.value })}
            />
          </div>
        )}

        <div className="policy-filter__group">
          <Label variant="caption" className="policy-filter__label">Статус</Label>
          <Select
            size="sm"
            value={STATUS_OPTIONS.find((o) => o.value === filters.status)?.label ?? 'Активные'}
            options={STATUS_OPTIONS}
            getOptionKey={(o) => o.value}
            getOptionLabel={(o) => o.label}
            onSelect={(o) => onChange({ ...filters, status: o.value })}
          />
        </div>

        <div className="policy-filter__group">
          <Label variant="caption" className="policy-filter__label">Продукт</Label>
          <Select
            size="sm"
            value={productOptions.find((o) => o.value === filters.product)?.label ?? 'Все'}
            options={productOptions}
            getOptionKey={(o) => o.value}
            getOptionLabel={(o) => o.label}
            onSelect={(o) => onChange({ ...filters, product: o.value })}
          />
        </div>

        {hasActiveFilters && (
          <Button variant="secondary" size="sm" onClick={onReset}>
            Сбросить
          </Button>
        )}
      </div>
    </div>
  );
};

export default AgreementsFilter;
